import React, { useState, useEffect } from 'react';
import { AssistantToolConfig } from '../types/assistant';
import { AVAILABLE_TOOLS, Tool, ToolOperation } from '../types/tools';
import { googleCalendarService } from '../services/google-calendar-service';
import { googleDriveService } from '../services/google-drive-service';
import { saiposService } from '../services/saipos-service';
import { useToast } from '../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';

interface AssistantToolsConfigProps {
  assistantId: string;
  userId: string;
  tools: AssistantToolConfig[];
  onToolsChange: (tools: AssistantToolConfig[]) => void;
}

export function AssistantToolsConfig({ 
  assistantId, 
  userId, 
  tools, 
  onToolsChange 
}: AssistantToolsConfigProps) {
  const [expandedTool, setExpandedTool] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<Record<string, boolean>>({});
  const [connecting, setConnecting] = useState<string | null>(null);
  const [showSaiposTokenModal, setShowSaiposTokenModal] = useState(false);
  const [saiposToken, setSaiposToken] = useState('');
  const [saiposStoreId, setSaiposStoreId] = useState('');
  const [savingToken, setSavingToken] = useState(false);
  const toast = useToast();

  // Verificar status de conexão das integrações
  useEffect(() => {
    const checkConnections = async () => {
      const status: Record<string, boolean> = {};
      
      status['google_calendar'] = await googleCalendarService.isConnected(userId);
      status['google_drive'] = await googleDriveService.isConnected(userId);
      status['saipos'] = await saiposService.isConnected(userId);
      
      setConnectionStatus(status);

      // Carregar configuração salva do Saipos se estiver conectado
      if (status['saipos']) {
        try {
          // Usar o método interno através de uma chamada ao Supabase diretamente
          const { supabase } = await import('../lib/supabase');
          const { data } = await supabase
            .from('user_integrations')
            .select('provider_user_id')
            .eq('user_id', userId)
            .eq('provider', 'saipos')
            .eq('is_active', true)
            .single();
          
          if (data?.provider_user_id) {
            setSaiposStoreId(data.provider_user_id);
          }
        } catch (error) {
          console.error('Erro ao carregar store_id:', error);
        }
      }
    };

    checkConnections();
  }, [userId]);

  const getToolConfig = (toolId: string): AssistantToolConfig | undefined => {
    return tools.find(t => t.toolId === toolId);
  };

  const isToolEnabled = (toolId: string): boolean => {
    const config = getToolConfig(toolId);
    return config?.isActive ?? false;
  };

  const isOperationEnabled = (toolId: string, operationId: string): boolean => {
    const config = getToolConfig(toolId);
    return config?.enabledOperations?.includes(operationId) ?? false;
  };

  const toggleTool = (toolId: string) => {
    const existingConfig = getToolConfig(toolId);
    const tool = AVAILABLE_TOOLS.find(t => t.id === toolId);
    
    if (!tool) return;

    if (existingConfig) {
      // Toggle existing
      const updatedTools = tools.map(t => 
        t.toolId === toolId 
          ? { ...t, isActive: !t.isActive }
          : t
      );
      onToolsChange(updatedTools);
    } else {
      // Add new with all operations enabled
      const newConfig: AssistantToolConfig = {
        toolId,
        enabledOperations: tool.operations.map(op => op.id),
        isActive: true
      };
      onToolsChange([...tools, newConfig]);
    }
  };

  const toggleOperation = (toolId: string, operationId: string) => {
    const existingConfig = getToolConfig(toolId);
    const tool = AVAILABLE_TOOLS.find(t => t.id === toolId);
    
    if (!tool) return;

    if (existingConfig) {
      const operations = existingConfig.enabledOperations.includes(operationId)
        ? existingConfig.enabledOperations.filter(id => id !== operationId)
        : [...existingConfig.enabledOperations, operationId];
      
      const updatedTools = tools.map(t => 
        t.toolId === toolId 
          ? { ...t, enabledOperations: operations }
          : t
      );
      onToolsChange(updatedTools);
    } else {
      // Add new with this operation
      const newConfig: AssistantToolConfig = {
        toolId,
        enabledOperations: [operationId],
        isActive: true
      };
      onToolsChange([...tools, newConfig]);
    }
  };

  const handleConnect = async (toolId: string) => {
    setConnecting(toolId);
    
    try {
      if (toolId === 'google_calendar') {
        await googleCalendarService.getAccessToken(userId);
        toast.success('Google Calendar conectado com sucesso!');
      } else if (toolId === 'google_drive') {
        await googleDriveService.selectAndDownloadFiles(userId);
        toast.success('Google Drive conectado com sucesso!');
      } else if (toolId === 'saipos') {
        setShowSaiposTokenModal(true);
        setConnecting(null);
        return;
      }
      
      setConnectionStatus(prev => ({ ...prev, [toolId]: true }));
    } catch (error: any) {
      console.error('Erro ao conectar:', error);
      toast.error(error.message || 'Erro ao conectar');
    } finally {
      setConnecting(null);
    }
  };

  const handleSaveSaiposToken = async () => {
    if (!saiposToken.trim()) {
      toast.error('Por favor, insira o token do Saipos');
      return;
    }

    setSavingToken(true);
    try {
      // Usar saveConfig que aceita token e storeId
      await saiposService.saveConfig(userId, saiposToken.trim(), saiposStoreId.trim() || undefined);
      toast.success('Configuração do Saipos salva com sucesso!');
      setShowSaiposTokenModal(false);
      setSaiposToken('');
      setSaiposStoreId('');
      setConnectionStatus(prev => ({ ...prev, saipos: true }));
    } catch (error: any) {
      console.error('Erro ao salvar configuração:', error);
      toast.error(error.message || 'Erro ao salvar configuração');
    } finally {
      setSavingToken(false);
    }
  };

  const handleDisconnect = async (toolId: string) => {
    try {
      if (toolId === 'google_calendar') {
        await googleCalendarService.disconnect(userId);
      } else if (toolId === 'google_drive') {
        await googleDriveService.disconnect(userId);
      } else if (toolId === 'saipos') {
        await saiposService.disconnect(userId);
      }
      
      setConnectionStatus(prev => ({ ...prev, [toolId]: false }));
      toast.success('Desconectado com sucesso');
    } catch (error) {
      console.error('Erro ao desconectar:', error);
      toast.error('Erro ao desconectar');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Ferramentas do Agente
        </h3>
        <p className="text-sm text-gray-600">
          Configure quais ferramentas e operações este agente pode executar durante as conversas.
        </p>
      </div>

      <div className="space-y-4">
        {AVAILABLE_TOOLS.map((tool) => (
          <div 
            key={tool.id}
            className="border border-gray-200 rounded-lg overflow-hidden"
          >
            {/* Header da ferramenta */}
            <div 
              className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => setExpandedTool(expandedTool === tool.id ? null : tool.id)}
            >
              <div className="flex items-center gap-3">
                {tool.icon.startsWith('/') || tool.icon.startsWith('http') ? (
                  <img 
                    src={tool.icon} 
                    alt={tool.name}
                    className="w-8 h-8 object-contain"
                  />
                ) : (
                  <span className="text-2xl w-8 h-8 flex items-center justify-center">{tool.icon}</span>
                )}
                <div>
                  <h4 className="font-medium text-gray-900">{tool.name}</h4>
                  <p className="text-sm text-gray-500">{tool.description}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {/* Status de conexão */}
                {tool.requiresAuth && (
                  <div className="flex items-center gap-2">
                    {connectionStatus[tool.id] ? (
                      <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        Conectado
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                        <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                        Desconectado
                      </span>
                    )}
                  </div>
                )}

                {/* Toggle */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleTool(tool.id);
                  }}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    isToolEnabled(tool.id) ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      isToolEnabled(tool.id) ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>

                {/* Expand icon */}
                <svg 
                  className={`w-5 h-5 text-gray-400 transition-transform ${expandedTool === tool.id ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {/* Conteúdo expandido */}
            {expandedTool === tool.id && (
              <div className="p-4 border-t border-gray-200 bg-white">
                {/* Botão de conectar/desconectar */}
                {tool.requiresAuth && (
                  <div className="mb-4 pb-4 border-b border-gray-100">
                    {connectionStatus[tool.id] ? (
                      <button
                        type="button"
                        onClick={() => handleDisconnect(tool.id)}
                        className="px-4 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                      >
                        Desconectar {tool.name}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleConnect(tool.id)}
                        disabled={connecting === tool.id}
                        className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                      >
                        {connecting === tool.id ? (
                          <>
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Conectando...
                          </>
                        ) : (
                          <>
                            {tool.icon.startsWith('/') || tool.icon.startsWith('http') ? (
                              <img src={tool.icon} alt="" className="w-4 h-4" />
                            ) : (
                              <span className="text-sm">{tool.icon}</span>
                            )}
                            Conectar {tool.name}
                          </>
                        )}
                      </button>
                    )}
                  </div>
                )}

                {/* Lista de operações */}
                <div>
                  <h5 className="text-sm font-medium text-gray-700 mb-3">
                    Operações disponíveis
                  </h5>
                  <div className="space-y-2">
                    {tool.operations.map((operation) => (
                      <label
                        key={operation.id}
                        className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:border-gray-200 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={isOperationEnabled(tool.id, operation.id)}
                          onChange={() => toggleOperation(tool.id, operation.id)}
                          disabled={!isToolEnabled(tool.id)}
                          className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 text-sm">
                            {operation.name}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {operation.description}
                          </div>
                          {operation.parameters.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {operation.parameters
                                .filter(p => p.required)
                                .map(param => (
                                  <span 
                                    key={param.name}
                                    className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded"
                                  >
                                    {param.name}
                                  </span>
                                ))
                              }
                            </div>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Placeholder para MCP */}
      <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
        <div className="text-3xl mb-2">🔧</div>
        <h4 className="font-medium text-gray-900 mb-1">Adicionar Ferramenta via MCP</h4>
        <p className="text-sm text-gray-500 mb-3">
          Conecte ferramentas personalizadas usando Model Context Protocol
        </p>
        <button
          type="button"
          disabled
          className="px-4 py-2 text-sm text-gray-500 bg-gray-100 rounded-lg cursor-not-allowed"
        >
          Em breve
        </button>
      </div>

      {/* Modal para configurar token do Saipos */}
      <Dialog open={showSaiposTokenModal} onOpenChange={setShowSaiposTokenModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Configurar Token do Saipos</DialogTitle>
            <DialogDescription>
              Insira o token de autenticação da API do Saipos. O token será salvo de forma segura e não poderá ser visualizado depois.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label htmlFor="saipos-token" className="block text-sm font-medium text-gray-700 mb-2">
                Token da API Saipos *
              </label>
              <input
                id="saipos-token"
                type="password"
                value={saiposToken}
                onChange={(e) => setSaiposToken(e.target.value)}
                placeholder="Cole seu token aqui"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
              />
              <p className="mt-2 text-xs text-gray-500">
                Você pode encontrar o token no painel do Saipos em Configurações → Integrações → API
              </p>
            </div>
            
            <div>
              <label htmlFor="saipos-store-id" className="block text-sm font-medium text-gray-700 mb-2">
                ID da Loja (Opcional)
              </label>
              <input
                id="saipos-store-id"
                type="text"
                value={saiposStoreId}
                onChange={(e) => setSaiposStoreId(e.target.value)}
                placeholder="ID da loja no Saipos"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="mt-2 text-xs text-gray-500">
                Se informado, será usado como padrão em todas as consultas. Você ainda pode especificar um ID diferente em cada operação.
              </p>
            </div>
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={() => {
                setShowSaiposTokenModal(false);
                setSaiposToken('');
                setSaiposStoreId('');
              }}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSaveSaiposToken}
              disabled={savingToken || !saiposToken.trim()}
              className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {savingToken ? (
                <>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Salvando...
                </>
              ) : (
                'Salvar Token'
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

